import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { FlowLedger } from "./pages/FlowLedger";
import { SIMVault } from "./pages/SIMVault";
import { Pricing } from "./pages/Pricing";
import { FAQ } from "./pages/FAQ";
import { Contact } from "./pages/Contact";

// Merchant Dashboard imports
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { AuthCallback } from "./app/AuthCallback";
import { Login } from "./app/Login";
import { Register } from "./app/Register";
import { MerchantSetup } from "./app/MerchantSetup";
import { DashboardLayout } from "./app/DashboardLayout";
import { Dashboard } from "./app/Dashboard";
import { NewTransaction } from "./app/NewTransaction";
import { TransactionHistory } from "./app/TransactionHistory";
import { TransactionDetail } from "./app/TransactionDetail";
import { Reports } from "./app/Reports";
import { AuditLog } from "./app/AuditLog";
import { Staff } from "./app/Staff";
import { ProtectedRoute } from "./app/ProtectedRoute";
import { Profile as MerchantProfile } from "./app/Profile";
import { PaymentSources } from "./app/PaymentSources";
import { Notifications } from "./app/Notifications";
import { QRPaymentGenerator } from "./app/QRPaymentGenerator";
import { Checkout } from "./app/Checkout";
import { POSCheckout } from "./app/POSCheckout";
import { Products } from "./app/Products";
import { Branches } from "./app/Branches";

// Public Pages
import { PaymentPage } from "./PaymentPage";

// Admin Dashboard imports
import { AdminAuthProvider } from "./admin/AdminAuthContext";
import { AdminThemeProvider } from "./admin/AdminThemeContext";
import { AdminAuthCallback } from "./admin/AdminAuthCallback";
import { AdminLogin } from "./admin/AdminLogin";
import { AdminLayout } from "./admin/AdminLayout";
import { AdminDashboard } from "./admin/AdminDashboard";
import { AdminProtectedRoute } from "./admin/AdminProtectedRoute";
import { Merchants as AdminMerchants } from "./admin/Merchants";
import { Transactions as AdminTransactions } from "./admin/Transactions";
import { AuditLogs as AdminAuditLogs } from "./admin/AuditLogs";
import { StaffManagement as AdminStaffManagement } from "./admin/StaffManagement";
import { SMSInbox } from "./admin/SMSInbox";
import { SMSReviewQueue } from "./admin/SMSReviewQueue";
import { WebhookKeys } from "./admin/WebhookKeys";
import { IntegrationGuide } from "./admin/IntegrationGuide";
import { TestSMS } from "./admin/TestSMS";
import { AdminProfile } from "./admin/AdminProfile";
import { SIMVaultMonitor } from "./admin/SIMVaultMonitor";
import { PaymentSimulator } from "./admin/PaymentSimulator";

// Router component to handle auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    // Determine if this is admin or merchant auth callback
    if (location.pathname.startsWith('/admin')) {
      return <AdminAuthCallback />;
    }
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Marketing Site Routes */}
      <Route path="/" element={<MarketingLayout><Home /></MarketingLayout>} />
      <Route path="/about" element={<MarketingLayout><About /></MarketingLayout>} />
      <Route path="/flowledger" element={<MarketingLayout><FlowLedger /></MarketingLayout>} />
      <Route path="/simvault" element={<MarketingLayout><SIMVault /></MarketingLayout>} />
      <Route path="/pricing" element={<MarketingLayout><Pricing /></MarketingLayout>} />
      <Route path="/faq" element={<MarketingLayout><FAQ /></MarketingLayout>} />
      <Route path="/contact" element={<MarketingLayout><Contact /></MarketingLayout>} />

      {/* Public Payment Page (no auth required) */}
      <Route path="/pay/:reference" element={<PaymentPage />} />

      {/* Login shortcuts */}
      <Route path="/login" element={<Navigate to="/app/login" replace />} />
      <Route path="/register" element={<Navigate to="/app/register" replace />} />

      {/* Merchant Dashboard Routes */}
      <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/app/login" element={<Login />} />
      <Route path="/app/register" element={<Register />} />
      <Route path="/app/setup" element={
        <ProtectedRoute>
          <MerchantSetup />
        </ProtectedRoute>
      } />
      
      <Route path="/app" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="transactions/new" element={<NewTransaction />} />
        <Route path="transactions/:transactionId" element={<TransactionDetail />} />
        <Route path="transactions" element={<TransactionHistory />} />
        <Route path="reports" element={<Reports />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="staff" element={<Staff />} />
        <Route path="branches" element={<Branches />} />
        <Route path="profile" element={<MerchantProfile />} />
        <Route path="payment-sources" element={<PaymentSources />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="qr-payments" element={<QRPaymentGenerator />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="pos" element={<POSCheckout />} />
        <Route path="products" element={<Products />} />
      </Route>

      {/* Admin Dashboard Routes */}
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      
      <Route path="/admin" element={
        <AdminThemeProvider>
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        </AdminThemeProvider>
      }>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="merchants" element={<AdminMerchants />} />
        <Route path="transactions" element={<AdminTransactions />} />
        <Route path="sms-inbox" element={<SMSInbox />} />
        <Route path="sms-review" element={<SMSReviewQueue />} />
        <Route path="webhook-keys" element={<WebhookKeys />} />
        <Route path="integration-guide" element={<IntegrationGuide />} />
        <Route path="test-sms" element={<TestSMS />} />
        <Route path="simvault" element={<SIMVaultMonitor />} />
        <Route path="payment-simulator" element={<PaymentSimulator />} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
        <Route path="users" element={<AdminStaffManagement />} />
        <Route path="profile" element={<AdminProfile />} />
      </Route>
    </Routes>
  );
}

// Marketing site layout wrapper
function MarketingLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <ThemeProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <AuthProvider>
              <AdminAuthProvider>
                <AppRouter />
              </AdminAuthProvider>
            </AuthProvider>
          </BrowserRouter>
        </CurrencyProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
